import React, { useState, useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Mail, Bell, CheckCircle, XCircle, Clock, Send, ClipboardList, FileSignature, CalendarDays, MapPin, MessageSquare, AlertTriangle, Building2, PartyPopper, Ban } from 'lucide-react';
import useRecetas from '../hooks/usePuedeEmitirRecetas';
import { ARGENTINE_HOLIDAYS_2025 } from '../utils/argentineHolidays';
import { getExtraLabel, formatExtraValue } from '../utils/patientExtraData';
import { authFetch } from '../services/api';
import ConfirmModal from './ConfirmModal';
import RescheduleModal from './RescheduleModal';
import DayTimelineView, { cierreDelDia } from './DayTimelineView';
import AppointmentNoteEditor from './AppointmentNoteEditor';
import DetalleTurno from './DetalleTurno';
import type { QuickAddData, ServiceOption, SpecialDayInfo, OpenSpecialDayData, DiaCerradoInfo } from './DayTimelineView';
import MiniCalendarSlide from './MiniCalendarSlide';
import './MobileMonthCalendar.css';

// =====================================================
// INTERFACES
// =====================================================

interface NotificationLog {
  status: string;
  template_name: string;
  created_at: string;
  error_message: string | null;
}

interface CalendarAppointment {
  id: number;
  professional_id: number;
  client_name?: string;
  client_display_name: string;
  client_email?: string;
  client_phone?: string;
  service_name: string;
  service_category?: string;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  price: number;
  deposit_required?: boolean;
  deposit_amount?: number;
  deposit_paid?: boolean;
  notes?: string;
  professional_notes?: string;
  is_first_time?: boolean;
  is_rescheduled?: boolean;
  rescheduled_from?: string;
  urgency_level?: 'low' | 'normal' | 'high';
  created_at?: string;
  updated_at?: string;
  cancellation_reason?: string;
  location_id?: number;
  location_name?: string;
  location_city?: string;
  patient_extra_data?: Record<string, any>;
  metodo_pago_elegido?: string;
  pago_verificado?: boolean;
  // Notification tracking
  reminder_sent?: boolean;
  reminder_sent_at?: string;
  whatsapp_confirmation?: NotificationLog | null;
  whatsapp_reminder?: NotificationLog | null;
  email_confirmation?: NotificationLog | null;
  email_reminder?: NotificationLog | null;
}

// Helper: parsear fecha como local (evita desfase UTC)
const parseLocalDate = (dateStr: string): Date => {
  const parts = dateStr.split('T')[0].split('-');
  return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12);
};

// Helper: mostrar nombre multi-servicio si existe
const getServiceDisplay = (apt: CalendarAppointment): string => {
  try {
    const extra = apt.patient_extra_data;
    if (extra && extra.all_service_names) return extra.all_service_names;
  } catch (e) {}
  return apt.service_name || 'Consulta';
};

interface ProfessionalSchedule {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_available: boolean;
  break_start_time?: string | null;
  break_end_time?: string | null;
  location_id?: number | null;
}

interface Holiday {
  date: string;
  name: string;
}

interface User {
  id: number;
  professional_type: 'salud' | 'belleza';
  nombre: string;
  apellido: string;
  business_name?: string;
}

interface WorkSchedule {
  day_of_week: number;
  start_time: string;
  end_time: string;
  break_start?: string;
  break_end?: string;
  is_working: boolean;
}

// Tipos de vista
type ViewMode = 'month' | 'agenda' | 'list';

// =====================================================
// UTILIDADES
// =====================================================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getToday = () => {
  const today = new Date();
  return {
    year: today.getFullYear(),
    month: today.getMonth(),
    day: today.getDate()
  };
};

const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (year: number, month: number) => {
  return new Date(year, month, 1).getDay();
};

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DAYS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0
  }).format(price);
};

const getStatusStyle = (status: string): string => {
  switch (status) {
    case 'confirmed': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'pending': return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'completed': return 'bg-sky-100 text-sky-700 border-sky-200';
    case 'cancelled': return 'bg-gray-200 text-gray-500 border-gray-200';
    case 'no_show': return 'bg-gray-100 text-gray-600 border-gray-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};

const getStatusText = (status: string): string => {
  const statusMap = {
    pending: 'Pendiente',
    confirmed: 'Confirmada',
    completed: 'Completada',
    cancelled: 'Cancelada',
    no_show: 'No asistió'
  };
  return statusMap[status as keyof typeof statusMap] || status;
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

interface MobileMonthCalendarProps {
  overrideUser?: User | null;
}

// =====================================================
// COMPONENTE DE INDICADORES DE NOTIFICACIÓN
// =====================================================
const NotificationIndicators: React.FC<{ appointment: CalendarAppointment; compact?: boolean }> = ({ appointment: apt, compact = false }) => {
  if (compact) {
    return (
      <div className="flex items-center gap-1 mt-1">
        {apt.whatsapp_confirmation?.status === 'sent' && <MessageCircle className="w-3 h-3 text-green-500" />}
        {apt.whatsapp_confirmation && apt.whatsapp_confirmation.status !== 'sent' && <MessageCircle className="w-3 h-3 text-red-400" />}
        {(apt.whatsapp_reminder?.status === 'sent' || apt.reminder_sent) && <Bell className="w-3 h-3 text-blue-500" />}
        {apt.whatsapp_reminder && apt.whatsapp_reminder.status !== 'sent' && <Bell className="w-3 h-3 text-red-400" />}
        {apt.email_confirmation?.status === 'sent' && <Mail className="w-3 h-3 text-emerald-500" />}
        {apt.email_confirmation && apt.email_confirmation.status !== 'sent' && <Mail className="w-3 h-3 text-red-400" />}
        {apt.email_reminder?.status === 'sent' && <Send className="w-3 h-3 text-teal-500" />}
        {apt.email_reminder && apt.email_reminder.status !== 'sent' && <Send className="w-3 h-3 text-red-400" />}
      </div>
    );
  }

  return (
    <div className="space-y-1.5 mt-3">
      {apt.whatsapp_confirmation ? (
        apt.whatsapp_confirmation.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-green-600">
            <MessageCircle className="w-3.5 h-3.5" /><CheckCircle className="w-3 h-3" /><span>WA confirmación enviada</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500">
            <MessageCircle className="w-3.5 h-3.5" /><XCircle className="w-3 h-3" /><span>Error WA confirmación</span>
          </div>
        )
      ) : apt.client_phone ? (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <MessageCircle className="w-3.5 h-3.5" /><Clock className="w-3 h-3" /><span>Sin confirmación WA</span>
        </div>
      ) : null}

      {apt.whatsapp_reminder ? (
        apt.whatsapp_reminder.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-blue-600">
            <Bell className="w-3.5 h-3.5" /><CheckCircle className="w-3 h-3" /><span>Recordatorio WA enviado</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500">
            <Bell className="w-3.5 h-3.5" /><XCircle className="w-3 h-3" /><span>Error recordatorio WA</span>
          </div>
        )
      ) : apt.reminder_sent ? (
        <div className="flex items-center gap-1.5 text-xs text-blue-600">
          <Bell className="w-3.5 h-3.5" /><CheckCircle className="w-3 h-3" /><span>Recordatorio enviado</span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Bell className="w-3.5 h-3.5" /><Clock className="w-3 h-3" /><span>Recordatorio pendiente</span>
        </div>
      )}

      {apt.email_confirmation ? (
        apt.email_confirmation.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-emerald-600">
            <Mail className="w-3.5 h-3.5" /><CheckCircle className="w-3 h-3" /><span>Email confirmación enviado</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500">
            <Mail className="w-3.5 h-3.5" /><XCircle className="w-3 h-3" /><span>Error email confirmación</span>
          </div>
        )
      ) : apt.client_email ? (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Mail className="w-3.5 h-3.5" /><Clock className="w-3 h-3" /><span>Sin email confirmación</span>
        </div>
      ) : null}

      {apt.email_reminder ? (
        apt.email_reminder.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-teal-600">
            <Send className="w-3.5 h-3.5" /><CheckCircle className="w-3 h-3" /><span>Email recordatorio enviado</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500">
            <Send className="w-3.5 h-3.5" /><XCircle className="w-3 h-3" /><span>Error email recordatorio</span>
          </div>
        )
      ) : (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Send className="w-3.5 h-3.5" /><Clock className="w-3 h-3" /><span>Email rec. pendiente</span>
        </div>
      )}
    </div>
  );
};

const MobileMonthCalendar: React.FC<MobileMonthCalendarProps> = ({ overrideUser }) => {
  const today = getToday();
  const [currentYear, setCurrentYear] = useState(today.year);
  const [currentMonth, setCurrentMonth] = useState(today.month);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  
  // ✅ ESTADO DE VISTA (guardado en localStorage)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const savedView = localStorage.getItem('mobileCalendarViewMode');
    return (savedView as ViewMode) || 'month';
  });
  
  // ✅ ESTADO DE FECHA PARA VISTA AGENDA
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showMiniCalendar, setShowMiniCalendar] = useState(false);
  
  // Estados para datos
  const [appointments, setAppointments] = useState<CalendarAppointment[]>([]);
  const [workingSchedule, setWorkingSchedule] = useState<ProfessionalSchedule[]>([]);
  // Días especiales (fecha y sede) para pintarlos como días de atención en la
  // grilla, y el de la fecha elegida para la agenda del día
  const [specialDates, setSpecialDates] = useState<{ fecha: string; sede: number | null }[]>([]);
  const [specialDay, setSpecialDay] = useState<SpecialDayInfo | null>(null);
  const [diaCerrado, setDiaCerrado] = useState<DiaCerradoInfo | null>(null);
  const [recargarEspecial, setRecargarEspecial] = useState(0);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [filterLocation, setFilterLocation] = useState<number | 'all'>('all');
  // Sedes ACTIVAS reales del profesional (para el selector; antes se derivaban de las citas)
  const [sedes, setSedes] = useState<{ id: number; name: string; city?: string }[]>([]);
  
  // ✅ ESTADO PARA DETALLE DE CITA
  const [selectedAppointment, setSelectedAppointment] = useState<CalendarAppointment | null>(null);
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(false);
  const [confirmHideOpen, setConfirmHideOpen] = useState(false); // "Borrar" cancelado
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  // ✅ BLOQUEO DE HORARIOS Y SOBRETURNOS
  const [maxOverridesPerSlot, setMaxOverridesPerSlot] = useState(0);
  const [agendaServices, setAgendaServices] = useState<ServiceOption[]>([]);
  const [blockedHours, setBlockedHours] = useState<{ blockedFrom: string; blockedUntil?: string; reason?: string } | null>(null);
  // Lista global de fechas con bloqueos (para indicador en vista mes)
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [pendingTransferAppointment, setPendingTransferAppointment] = useState<CalendarAppointment | null>(null);
  const navigate = useNavigate();
  // Gobierna el boton "Crear receta" del detalle de turno.
  const { mostrar: mostrarBotonReceta, puedeEmitir: puedeEmitirRecetas } = useRecetas();

  // ✅ FUNCIÓN PARA CAMBIAR VISTA (guarda en localStorage)
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('mobileCalendarViewMode', mode);
  };

  // Cargar datos del usuario y backend
  useEffect(() => {
    const loadData = async () => {
      try {
        // Si viene overrideUser (asistente viendo calendario de un profesional)
        let userData: any;
        if (overrideUser) {
          userData = overrideUser;
        } else {
          userData = JSON.parse(localStorage.getItem('user') || '{}');
        }
        setUser(userData);

        if (!userData.id) {
          console.error('No user ID found');
          setLoading(false);
          return;
        }

        // Cargar citas (con auto-refresh de token)
        const aptsResponse = await authFetch(
          `${API_BASE_URL}/api/appointments/professional/${userData.id}`
        );

        if (aptsResponse.ok) {
          const aptsData = await aptsResponse.json();
          console.log('📅 Citas cargadas:', aptsData.appointments?.length || 0);
          setAppointments(aptsData.appointments || []);
        }

        // Cargar sedes ACTIVAS reales (para el selector de sedes)
        try {
          const locRes = await authFetch(`${API_BASE_URL}/api/locations/professional/${userData.id}`);
          if (locRes.ok) {
            const locData = await locRes.json();
            const list = locData.locations || [];
            setSedes(
              list
                .filter((l: any) => l.is_active !== false)
                .map((l: any) => ({ id: l.id, name: l.name, city: l.city }))
            );
          }
        } catch (locErr) { console.error('Error cargando sedes:', locErr); }

        // Cargar horarios. Antes pedía /api/schedule/:id, que no existe (404):
        // el horario semanal nunca se cargaba en el celular, la grilla no
        // marcaba los días de atención y la agenda dejaba tocar cualquier
        // hora. Es la misma ruta que usa el calendario de la computadora.
        const schedResponse = await authFetch(
          `${API_BASE_URL}/api/schedules/professional/${userData.id}`
        );

        if (schedResponse.ok) {
          const schedData = await schedResponse.json();
          console.log('⏰ Horarios cargados:', schedData.schedules?.length || 0);
          setWorkingSchedule(schedData.schedules || []);
        }

        // Cargar configuración de sobreturnos
        try {
          const configRes = await fetch(`${API_BASE_URL}/api/schedule/professional/${userData.id}/config/public`);
          if (configRes.ok) {
            const configData = await configRes.json();
            if (configData.success && configData.scheduleConfig) {
              setMaxOverridesPerSlot(configData.scheduleConfig.maxOverridesPerSlot || 0);
            }
          }
        } catch (cfgErr) { console.error('Error cargando config sobreturnos:', cfgErr); }

        // Cargar servicios para QuickAdd
        try {
          const svcRes = await authFetch(`${API_BASE_URL}/api/services/professional/${userData.id}/services`);
          if (svcRes.ok) {
            const svcData = await svcRes.json();
            if (svcData.success && svcData.services) {
              setAgendaServices(svcData.services.map((s: any) => ({
                id: s.id, name: s.name, duration: s.duration, price: s.price
              })));
            }
          }
        } catch (svcErr) { console.error('Error cargando servicios:', svcErr); }

        setLoading(false);
      } catch (error) {
        console.error('Error cargando datos calendario móvil:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [overrideUser]);

  const professionalType = user?.professional_type || 'salud';

  // Sedes únicas para filtro
  const uniqueLocations = (() => {
    const locMap = new Map<number, { id: number; name: string; city?: string }>();
    appointments.forEach(apt => {
      if (apt.location_id && apt.location_name && !locMap.has(apt.location_id)) {
        locMap.set(apt.location_id, { id: apt.location_id, name: apt.location_name, city: apt.location_city });
      }
    });
    return Array.from(locMap.values());
  })();

  // El selector usa las sedes activas reales; fallback a las derivadas de citas.
  const sedeOptions = sedes.length > 0 ? sedes : uniqueLocations;

  // Appointments filtrados por sede
  const displayAppointments = filterLocation === 'all'
    ? appointments
    : appointments.filter(apt => apt.location_id === filterLocation);

  // Colores según tipo
  const colors = professionalType === 'salud' 
    ? {
        primary: 'from-blue-500 via-cyan-500 to-green-500',
        working: 'bg-green-50 border-green-200 text-green-700',
        today: 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold',
        holiday: 'bg-red-50 border-red-200 text-red-700',
        badge: 'bg-gradient-to-r from-cyan-500 to-green-500',
        modalGradient: 'from-blue-500 via-cyan-500 to-green-500',
        button: 'bg-gradient-to-r from-blue-500 to-green-500'
      }
    : {
        primary: 'from-pink-500 to-purple-500',
        working: 'bg-green-50 border-green-200 text-green-700',
        today: 'bg-yellow-400 text-white font-bold',
        holiday: 'bg-red-50 border-red-200 text-red-700',
        badge: 'bg-pink-500',
        modalGradient: 'from-pink-500 to-purple-500',
        button: 'bg-gradient-to-r from-pink-500 to-purple-500'
      };

  // Obtener días del mes actual (solo para vista mes)
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

  // Crear array de días (solo para vista mes)
  const calendarDays: (number | null)[] = [];
  
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Funciones de navegación (solo para vista mes)
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
    setSelectedDate(today);
  };

  // Interceptar confirmación para turnos con transferencia pendiente
  const handleConfirmWithTransferCheck = (appointment: CalendarAppointment) => {
    if (
      appointment.status === 'pending' &&
      appointment.metodo_pago_elegido === 'transferencia' &&
      !appointment.pago_verificado
    ) {
      setPendingTransferAppointment(appointment);
      setShowTransferModal(true);
      return;
    }
    handleAppointmentAction(appointment.id, 'confirmed');
  };

  // Manejar acciones de citas (confirmar, completar, cancelar)
  const handleAppointmentAction = async (appointmentId: number, action: string, data?: any) => {
    if (!user?.id) return;
    
    setActionLoading(true);
    
    try {
      const response = await authFetch(
        `${API_BASE_URL}/api/appointments/professional/${user.id}/appointment/${appointmentId}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            status: action,
            professionalNotes: data?.professionalNotes,
            cancellationReason: data?.cancellationReason
          })
        }
      );
      
      const result = await response.json();
      
      if (result.success) {
        // Actualizar el estado local de appointments
        setAppointments(prev => prev.map(apt => 
          apt.id === appointmentId 
            ? { ...apt, status: action as any }
            : apt
        ));
        
        // Cerrar el modal
        setShowModal(false);
        setSelectedDay(null);
        setShowAppointmentDetail(false);
        
        alert('Cita actualizada exitosamente');
      } else {
        throw new Error(result.message || 'Error actualizando cita');
      }
    } catch (error: any) {
      console.error('Error ejecutando acción:', error);
      alert(`Error al actualizar la cita: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // "Borrar" un turno cancelado: soft-hide reversible (no se borra, queda registro)
  const handleHideAppointment = async () => {
    if (!selectedAppointment) return;
    setActionLoading(true);
    try {
      await authFetch(`${API_BASE_URL}/api/appointments/appointment/${selectedAppointment.id}/hide`, { method: 'PATCH' });
      setAppointments(prev => prev.filter(apt => apt.id !== selectedAppointment.id));
      setConfirmHideOpen(false);
      setShowAppointmentDetail(false);
    } catch (error: any) {
      console.error('Error ocultando turno:', error);
      alert('Error al borrar el turno');
    } finally {
      setActionLoading(false);
    }
  };

  // Obtener citas de un día específico (para vista mes)
  const getAppointmentsForDay = (day: number) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayApts = displayAppointments.filter(apt => {
      // Extraer solo la fecha (sin hora ni zona horaria)
      const aptDate = apt.appointment_date.split('T')[0];
      return aptDate === dateStr;
    });
    return dayApts;
  };

  // Verificar si un día es laborable (para vista mes)
  // Horario semanal de la sede filtrada (filas sin sede valen para todas)
  const horarioDeSede = filterLocation === 'all'
    ? workingSchedule
    : workingSchedule.filter(s => s.location_id == null || s.location_id === filterLocation);

  const isWorkingDay = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (specialDates.some((e) => e.fecha === dateStr && (filterLocation === 'all' || e.sede === null || e.sede === filterLocation))) {
      return true;
    }
    const dayOfWeek = date.getDay();
    return horarioDeSede.some(
      schedule => schedule.day_of_week === dayOfWeek && schedule.is_available
    );
  };

  // Verificar si es feriado (para vista mes)
  const isHoliday = (day: number): boolean => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return ARGENTINE_HOLIDAYS_2025.some(holiday => holiday.date === dateStr);
  };

  const getHolidayName = (day: number): string => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const holiday = ARGENTINE_HOLIDAYS_2025.find(h => h.date === dateStr);
    return holiday ? holiday.name : '';
  };

  // Obtener ícono según estado de la cita
  const getStatusIcon = (status: string): React.ReactNode => {
    switch(status) {
      case 'confirmed': return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'pending': return <Clock className="w-6 h-6 text-orange-500" />;
      case 'completed': return <CheckCircle className="w-6 h-6 text-blue-600" />;
      case 'cancelled': return <XCircle className="w-6 h-6 text-gray-400" />;
      case 'no_show': return <Ban className="w-6 h-6 text-gray-600" />;
      default: return <ClipboardList className="w-6 h-6 text-gray-600" />;
    }
  };

  // Obtener clase CSS para cada día (solo para vista mes)
  const getDayClass = (day: number): string => {
    const isToday = day === today.day && currentMonth === today.month && currentYear === today.year;
    const working = isWorkingDay(day);
    const holiday = isHoliday(day);
    
    let classes = 'relative aspect-square rounded-xl flex items-center justify-center cursor-pointer transition-all border-2 ';
    
    if (isToday) {
      classes += colors.today;
    } else if (holiday) {
      classes += colors.holiday;
    } else if (working) {
      classes += colors.working;
    } else {
      classes += 'bg-gray-50 border-gray-200 text-gray-400';
    }
    
    classes += ' hover:scale-105 hover:shadow-lg';
    
    return classes;
  };

  // Manejar click en un día (solo para vista mes)
  const handleDayClick = (day: number) => {
    const dayAppointments = getAppointmentsForDay(day);
    if (dayAppointments.length > 0) {
      setSelectedDay(day);
      setShowModal(true);
    }
  };

  // ✅ FUNCIONES PARA VISTA AGENDA
  const goToPrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };
  
  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  // ✅ FUNCIÓN PARA MANEJAR CLICK EN CITA (vista agenda)
  const handleAppointmentClick = (appointment: CalendarAppointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentDetail(true);
  };

  // ✅ FUNCIÓN PARA MANEJAR WHATSAPP (CORREGIDA)
  const handleWhatsAppClick = (appointment: CalendarAppointment) => {
    if (!appointment.client_phone) return;
    
    const professionalName = user?.business_name || `${user?.nombre} ${user?.apellido}`;
    const message = encodeURIComponent(
      `Hola ${appointment.client_display_name}, te contacto desde ${professionalName} sobre tu turno del ${parseLocalDate(appointment.appointment_date).toLocaleDateString('es-AR')} a las ${appointment.appointment_time.substring(0, 5)}.`
    );
    const phone = appointment.client_phone.replace(/\D/g, '');
    const formattedPhone = phone.startsWith('54') ? phone : `54${phone}`;
    window.open(`https://wa.me/${formattedPhone}?text=${message}`, '_blank');
  };

  // ✅ CARGAR BLOQUEOS DEL DÍA SELECCIONADO
  useEffect(() => {
    if (viewMode !== 'agenda' || !user?.id) return;
    const loadBlockedHours = async () => {
      try {
        const dateStr = selectedDate.toISOString().split('T')[0];
        const response = await authFetch(
          `${API_BASE_URL}/api/schedule/professional/${user.id}/exceptions?date=${dateStr}`
        );
        setDiaCerrado(null);
        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.exceptions)) setDiaCerrado(cierreDelDia(data.exceptions, dateStr));
          if (data.success && data.exceptions?.length > 0) {
            const blockedException = data.exceptions.find(
              (e: any) => {
                const exDate = typeof e.exception_date === 'string'
                  ? e.exception_date.split('T')[0]
                  : new Date(e.exception_date).toISOString().split('T')[0];
                return exDate === dateStr && e.exception_type === 'blocked_hours' && e.custom_end_time;
              }
            );
            if (blockedException) {
              setBlockedHours({ blockedFrom: blockedException.custom_end_time.substring(0, 5), reason: blockedException.reason });
              return;
            }
          }
        }

        // Sin bloqueo "resto del día": buscar bloqueo por rango (time_blocks)
        const rangeResponse = await authFetch(
          `${API_BASE_URL}/api/schedule/professional/${user.id}/time-blocks?date=${dateStr}`
        );
        if (rangeResponse.ok) {
          const rangeData = await rangeResponse.json();
          const block = (rangeData.timeBlocks || [])[0];
          if (block) {
            setBlockedHours({
              blockedFrom: String(block.start_time).substring(0, 5),
              blockedUntil: String(block.end_time).substring(0, 5),
              reason: block.reason
            });
            return;
          }
        }
        setBlockedHours(null);
      } catch (error) {
        console.error('Error cargando bloqueos:', error);
      }
    };
    loadBlockedHours();
  }, [selectedDate, user?.id, viewMode]);

  // ✅ CARGAR LISTA GLOBAL DE FECHAS CON BLOQUEOS (para indicador en vista mes)
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const loadAllBlocked = async () => {
      try {
        const response = await authFetch(
          `${API_BASE_URL}/api/schedule/professional/${user.id}/exceptions`
        );
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        const exceptions = data.exceptions || data || [];
        const ymd = (val: any) => {
          if (!val) return '';
          if (typeof val === 'string') return val.split('T')[0];
          try {
            const d = new Date(val);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
          } catch { return ''; }
        };
        const list = exceptions
          .filter((ex: any) => ex.exception_type === 'blocked_hours' && ex.exception_date)
          .map((ex: any) => ymd(ex.exception_date))
          .filter(Boolean);
        setSpecialDates(
          exceptions
            .filter((ex: any) => ex.exception_type === 'custom_hours' && ex.is_working && ex.exception_date)
            .map((ex: any) => ({ fecha: ymd(ex.exception_date), sede: ex.location_id ?? null }))
        );

        // Sumar fechas con bloqueos por rango (time_blocks)
        let rangeDates: string[] = [];
        try {
          const rangeResponse = await authFetch(
            `${API_BASE_URL}/api/schedule/professional/${user.id}/time-blocks`
          );
          if (rangeResponse.ok) {
            const rangeData = await rangeResponse.json();
            rangeDates = (rangeData.timeBlocks || [])
              .map((b: any) => ymd(b.block_date))
              .filter(Boolean);
          }
        } catch { /* opcional */ }

        if (cancelled) return;
        setBlockedDates(Array.from(new Set([...list, ...rangeDates])));
      } catch (err) { console.error('Error cargando fechas bloqueadas:', err); }
    };
    loadAllBlocked();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') loadAllBlocked();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.id, blockedHours, recargarEspecial]);

  // ✅ BLOQUEAR HORARIOS (untilTime opcional: rango; sin untilTime: resto del día)
  const handleBlockHours = async (fromTime: string, untilTime?: string, reason?: string): Promise<boolean> => {
    if (!user?.id) return false;
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await authFetch(
        `${API_BASE_URL}/api/schedule/professional/${user.id}/block-hours`,
        {
          method: 'POST',
          body: JSON.stringify({ date: dateStr, fromTime, untilTime, reason })
        }
      );
      if (response.ok) {
        setBlockedHours({ blockedFrom: fromTime, blockedUntil: untilTime, reason });
        // Recargar citas
        const aptsResponse = await authFetch(
          `${API_BASE_URL}/api/appointments/professional/${user.id}`
        );
        if (aptsResponse.ok) {
          const aptsData = await aptsResponse.json();
          setAppointments(aptsData.appointments || []);
        }
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error bloqueando horarios - Status:', response.status, errorData);
        alert(`Error al bloquear horarios: ${errorData.error || errorData.message || 'Error desconocido'}`);
        return false;
      }
    } catch (error) {
      console.error('Error bloqueando horarios:', error);
      alert('Error de conexión al bloquear horarios. Verificá tu conexión a internet.');
      return false;
    }
  };

  // ✅ DESBLOQUEAR HORARIOS
  const handleUnblockHours = async (): Promise<boolean> => {
    if (!user?.id) return false;
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await authFetch(
        `${API_BASE_URL}/api/schedule/professional/${user.id}/unblock-hours`,
        {
          method: 'POST',
          body: JSON.stringify({ date: dateStr })
        }
      );
      if (response.ok) {
        setBlockedHours(null);
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error desbloqueando horarios - Status:', response.status, errorData);
        alert(`Error al desbloquear horarios: ${errorData.error || errorData.message || 'Error desconocido'}`);
        return false;
      }
    } catch (error) {
      console.error('Error desbloqueando horarios:', error);
      alert('Error de conexión al desbloquear horarios.');
      return false;
    }
  };

  // ✅ DÍA ESPECIAL DE LA FECHA ELEGIDA (vista agenda). Fecha local, no
  // toISOString: de noche en Argentina eso ya es el día siguiente en UTC.
  const fechaLocal = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  useEffect(() => {
    if (viewMode !== 'agenda' || !user?.id) return;
    let cancelado = false;
    const dateStr = fechaLocal(selectedDate);
    (async () => {
      try {
        const r = await authFetch(`${API_BASE_URL}/api/schedule/professional/${user.id}/exceptions?date=${dateStr}`);
        if (!r.ok) return;
        const data = await r.json();
        const lista = (data.exceptions || []).filter((e: any) =>
          String(e.exception_date).slice(0, 10) === dateStr
          && e.exception_type === 'custom_hours' && e.is_working && e.custom_start_time && e.custom_end_time
        );
        const elegido = lista.find((e: any) => filterLocation !== 'all' && e.location_id === filterLocation) || lista[0];
        if (cancelado) return;
        setSpecialDay(elegido ? {
          id: elegido.id,
          start: String(elegido.custom_start_time).slice(0, 5),
          end: String(elegido.custom_end_time).slice(0, 5),
          breakStart: elegido.break_start ? String(elegido.break_start).slice(0, 5) : null,
          breakEnd: elegido.break_end ? String(elegido.break_end).slice(0, 5) : null,
          reason: elegido.reason || null,
        } : null);
      } catch {
        if (!cancelado) setSpecialDay(null);
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, user?.id, viewMode, filterLocation, recargarEspecial]);

  // ✅ ABRIR EL DÍA (día especial, igual al de Horarios > Días especiales).
  // Lo usan sobre todo las secretarias: el servidor ya se lo permitía, pero
  // en su panel no había dónde.
  const handleOpenSpecialDay = async (d: OpenSpecialDayData): Promise<boolean> => {
    if (!user?.id) return false;
    try {
      const response = await authFetch(`${API_BASE_URL}/api/schedule/professional/${user.id}/exception`, {
        method: 'POST',
        body: JSON.stringify({
          exceptionDate: fechaLocal(selectedDate),
          exceptionType: 'custom_hours',
          isWorking: true,
          customStartTime: d.start,
          customEndTime: d.end,
          customBreakStart: d.breakStart || null,
          customBreakEnd: d.breakEnd || null,
          reason: d.reason || 'Día especial',
          locationId: d.locationId ?? null
        })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setRecargarEspecial((n) => n + 1);
        return true;
      }
      alert(`No se pudo abrir el día: ${data.message || data.error || 'error desconocido'}`);
      return false;
    } catch (error) {
      console.error('Error abriendo el día:', error);
      alert('Error de conexión al abrir el día.');
      return false;
    }
  };

  // ✅ QUITAR EL DÍA ESPECIAL
  const handleRemoveSpecialDay = async (): Promise<boolean> => {
    if (!user?.id || !specialDay?.id) return false;
    try {
      const response = await authFetch(
        `${API_BASE_URL}/api/schedule/professional/${user.id}/exception/${specialDay.id}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        setSpecialDay(null);
        setRecargarEspecial((n) => n + 1);
        return true;
      }
      const data = await response.json().catch(() => ({}));
      alert(`No se pudo quitar el día especial: ${data.error || 'error desconocido'}`);
      return false;
    } catch (error) {
      console.error('Error quitando el día especial:', error);
      alert('Error de conexión al quitar el día especial.');
      return false;
    }
  };

  // ✅ AGREGAR TURNO / SOBRETURNO DESDE TIMELINE
  const handleAddAppointment = (date: string, time: string) => {
    const hasConflict = appointments.some(apt => {
      const aptDate = apt.appointment_date.split('T')[0];
      const aptTime = apt.appointment_time.substring(0, 5);
      return aptDate === date && aptTime === time && apt.status !== 'cancelled';
    });
    const params = new URLSearchParams({ date, time });
    if (hasConflict) params.set('override', 'true');
    navigate(`/professional/new-appointment?${params.toString()}`);
  };

  // ✅ QuickAdd: crear turno rápido desde timeline
  const handleQuickAdd = async (data: QuickAddData): Promise<boolean> => {
    try {
      const body: any = {
        professionalId: user?.id,
        appointmentDate: data.date,
        appointmentTime: data.time,
        clientName: data.clientName,
        clientPhone: data.clientPhone || null,
        notes: data.notes || null,
        isOverride: appointments.some(apt => {
          const aptDate = apt.appointment_date.split('T')[0];
          const aptTime = apt.appointment_time.substring(0, 5);
          return aptDate === data.date && aptTime === data.time && apt.status !== 'cancelled';
        })
      };
      if (data.serviceId) {
        body.serviceId = data.serviceId;
      } else {
        body.customServiceName = data.serviceName;
        body.customDuration = data.duration;
      }
      const res = await authFetch(`${API_BASE_URL}/api/assistants/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const result = await res.json();
      if (result.success) {
        // Recargar appointments
        if (user?.id) {
          const aptRes = await authFetch(`${API_BASE_URL}/api/appointments/professional/${user.id}`);
          if (aptRes.ok) {
            const aptData = await aptRes.json();
            if (aptData.success) setAppointments(aptData.appointments || []);
          }
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error en QuickAdd:', err);
      return false;
    }
  };

  // ✅ OBTENER HORARIO PARA VISTA AGENDA (CORREGIDA)
  const getDaySchedule = (date: Date) => {
    const dayOfWeek = date.getDay();
    const filas = horarioDeSede.filter(s => s.day_of_week === dayOfWeek);
    if (filas.length === 0) return null;
    const trabaja = filas.filter(s => s.is_available);
    const base = trabaja.length === 1 ? trabaja[0] : filas[0];
    if (trabaja.length <= 1) {
      return {
        day_of_week: dayOfWeek,
        start_time: base.start_time,
        end_time: base.end_time,
        break_start: base.break_start_time || undefined,
        break_end: base.break_end_time || undefined,
        is_working: trabaja.length === 1
      };
    }
    // Varias sedes atienden ese día ("Todas las sedes"): del primer inicio al
    // último fin, sin corte (el corte de una sede es horario de la otra)
    const hhmm = (t: string) => String(t).slice(0, 5);
    return {
      day_of_week: dayOfWeek,
      start_time: trabaja.map(s => hhmm(s.start_time)).sort()[0],
      end_time: trabaja.map(s => hhmm(s.end_time)).sort().slice(-1)[0],
      is_working: true
    };
  };

  // ✅ OBTENER FECHAS CON TURNOS PARA MINI CALENDAR (filtrado por sede)
  const appointmentDates = displayAppointments
    .filter(apt => apt.status !== 'cancelled')
    .map(apt => apt.appointment_date.split('T')[0]);

  // ✅ OBTENER DÍAS LABORALES
  const workingDays = horarioDeSede
    .filter(s => s.is_available)
    .map(s => s.day_of_week);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <LoadingSpinner size={48} label="Cargando calendario..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header principal con navegación y selector de vista */}
      <div className={`bg-gradient-to-r ${colors.primary} rounded-b-2xl p-6 shadow-xl`}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {viewMode === 'month' ? 'Calendario' :
               viewMode === 'agenda' ? 'Agenda del Día' : 'Lista de Turnos'}
            </h1>
            <p className="text-white/90 text-sm">
              {viewMode === 'month' ? `${MONTHS[currentMonth]} ${currentYear}` :
               viewMode === 'agenda' ? selectedDate.toLocaleDateString('es-AR', {
                 weekday: 'long',
                 day: 'numeric',
                 month: 'long'
               }) : 'Todos los turnos'}
            </p>
          </div>
        </div>

        {/* Filtro de sede (solo si hay más de una) */}
        {/* Entrada escalonada de abajo hacia arriba: cada bloque arranca 60ms
            después del anterior, así la pantalla se arma sola en vez de
            aparecer de golpe. */}
        {sedeOptions.length > 1 && (
          <div className="mb-2 relative animate-entrada-abajo" style={{ animationDelay: '0ms' }}>
            <select
              value={filterLocation === 'all' ? 'all' : filterLocation}
              onChange={(e) => setFilterLocation(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="w-full bg-white/20 backdrop-blur-sm text-white py-2 px-3 pr-8 rounded-xl font-medium appearance-none text-sm border border-white/40 shadow-sm"
            >
              <option value="all">Todas las sedes</option>
              {sedeOptions.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}{loc.city ? ` - ${loc.city}` : ''}</option>
              ))}
            </select>
            <div className="absolute right-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        )}

        {/* Selector de vista - cerca del calendario */}
        <div className="relative mb-3 animate-entrada-abajo" style={{ animationDelay: '60ms' }}>
          <select
            value={viewMode}
            onChange={(e) => handleViewModeChange(e.target.value as ViewMode)}
            className="w-full bg-white/20 backdrop-blur-sm text-white py-2 px-3 rounded-xl font-medium appearance-none pr-8 text-sm border border-white/30"
          >
            <option value="month">Vista Mes</option>
            <option value="agenda">Vista Agenda</option>
            <option value="list">Vista Lista</option>
          </select>
          <div className="absolute right-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Controles de navegación según vista */}
        <div className="flex items-center justify-between">
          {viewMode === 'month' ? (
            <>
              <button
                onClick={goToPreviousMonth}
                className="bg-white/20 backdrop-blur-sm text-white w-10 h-10 rounded-xl flex items-center justify-center hover:bg-white/30 transition-all"
              >
                ←
              </button>
              
              <button
                onClick={goToToday}
                className="bg-white/20 backdrop-blur-sm text-white py-2 px-4 rounded-xl font-medium hover:bg-white/30 transition-all"
              >
                Hoy
              </button>
              
              <button
                onClick={goToNextMonth}
                className="bg-white/20 backdrop-blur-sm text-white w-10 h-10 rounded-xl flex items-center justify-center hover:bg-white/30 transition-all"
              >
                →
              </button>
            </>
          ) : viewMode === 'agenda' ? (
            <>
              <button
                onClick={goToPrevDay}
                className="bg-white/20 backdrop-blur-sm text-white w-10 h-10 rounded-xl flex items-center justify-center hover:bg-white/30 transition-all"
              >
                ←
              </button>
              
              <button
                onClick={() => setShowMiniCalendar(true)}
                className="bg-white/30 backdrop-blur-sm border border-white/50 shadow-sm text-white py-2 px-5 rounded-xl font-bold hover:bg-white/40 transition-all flex items-center gap-1.5"
                title="Seleccionar otra fecha"
              >
                <span>{selectedDate.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}</span>
                <svg className="w-4 h-4 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              <button
                onClick={goToNextDay}
                className="bg-white/20 backdrop-blur-sm text-white w-10 h-10 rounded-xl flex items-center justify-center hover:bg-white/30 transition-all"
              >
                →
              </button>
            </>
          ) : (
            <button
              onClick={goToToday}
              className="bg-white/20 backdrop-blur-sm text-white py-2 px-4 rounded-xl font-medium hover:bg-white/30 transition-all mx-auto"
            >
              Ver Turnos de Hoy
            </button>
          )}
        </div>
      </div>

      {/* Contenido según vista seleccionada */}
      <div className="p-4">
        {/* Vista Mes (original) */}
        {viewMode === 'month' && (
          <>
            {/* Contador de citas del mes */}
            <div className="bg-white rounded-xl p-4 mb-4 shadow-md animate-entrada-abajo" style={{ animationDelay: '120ms' }}>
              <p className="text-center text-gray-700">
                <span className="font-bold text-2xl text-blue-600">
                  {appointments.filter(apt => {
                    const d = new Date(apt.appointment_date);
                    return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
                  }).length}
                </span>
                {' '}turnos en {MONTHS[currentMonth]}
              </p>
            </div>

            {/* Calendario */}
            <div className="bg-white rounded-2xl shadow-xl p-4 animate-entrada-abajo" style={{ animationDelay: '180ms' }}>
              {/* Encabezados de días */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {DAYS.map((day, index) => (
                  <div
                    key={index}
                    className="text-center text-sm font-bold text-gray-600 py-2"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Grid de días. Se despliega fila por fila al aparecer
                  (.cal-dia, en MobileMonthCalendar.css). Clave por posición y la
                  clase también en los huecos: al cambiar de mes las casillas se
                  reusan y la animación no se repite a medias (si un hueco pasa
                  a ser día y recién ahí recibe la clase, se anima solo él). */}
              <div className="grid grid-cols-7 gap-2">
                {calendarDays.map((day, index) => {
                  const despliegue = { ['--fila' as string]: Math.floor(index / 7), ['--col' as string]: index % 7 };
                  if (day === null) {
                    return <div key={index} className="aspect-square cal-dia" style={despliegue} />;
                  }

                  const dayAppointments = getAppointmentsForDay(day);
                  const appointmentCount = dayAppointments.length;
                  const holiday = isHoliday(day);
                  const dayDateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const isBlocked = blockedDates.includes(dayDateStr);

                  return (
                    <div
                      key={index}
                      onClick={() => handleDayClick(day)}
                      className={`${getDayClass(day)} cal-dia ${isBlocked ? 'ring-2 ring-red-400' : ''}`}
                      style={despliegue}
                    >
                      {/* Número del día */}
                      <span className="text-lg font-semibold">
                        {day}
                      </span>

                      {/* Badge con cantidad de turnos */}
                      {appointmentCount > 0 && (
                        <div className={`absolute -top-1 -right-1 ${colors.badge} text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center shadow-lg border-2 border-white`}>
                          {appointmentCount}
                        </div>
                      )}

                      {/* Indicador de horarios bloqueados */}
                      {isBlocked && (
                        <div
                          className="absolute -top-1 -left-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-[18px] h-[18px] flex items-center justify-center shadow-lg border-2 border-white"
                          title="Día con horarios bloqueados"
                        >
                          🚫
                        </div>
                      )}

                      {/* Ícono de feriado */}
                      {holiday && (
                        <div className="absolute bottom-1 text-xs">
                          <PartyPopper className="w-3 h-3 text-red-500" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Leyenda */}
              <div className="mt-6 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-100 border-2 border-green-200 rounded"></div>
                  <span className="text-gray-600">Día laborable</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-100 border-2 border-red-200 rounded"></div>
                  <span className="text-gray-600">Feriado / No laborable</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-400 rounded"></div>
                  <span className="text-gray-600">Hoy</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 ${colors.badge} rounded-full flex items-center justify-center text-white text-xs font-bold`}>
                    3
                  </div>
                  <span className="text-gray-600">Cantidad de turnos</span>
                </div>
                {blockedDates.length > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 ring-2 ring-red-400 rounded bg-white"></div>
                    <span className="text-gray-600">Horarios bloqueados</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Vista Agenda */}
        {viewMode === 'agenda' && (
          <div className="mt-4 cal-baja">
            {DayTimelineView ? (
              <DayTimelineView
                selectedDate={selectedDate}
                appointments={appointments}
                workSchedule={getDaySchedule(selectedDate)}
                onAppointmentClick={handleAppointmentClick}
                onWhatsAppClick={handleWhatsAppClick}
                professionalName={user?.business_name || `${user?.nombre} ${user?.apellido}`}
                blockedHours={blockedHours}
                onBlockHours={handleBlockHours}
                onUnblockHours={handleUnblockHours}
                specialDay={specialDay}
                diaCerrado={diaCerrado}
                onOpenSpecialDay={handleOpenSpecialDay}
                onRemoveSpecialDay={handleRemoveSpecialDay}
                sedes={sedes.map(sd => ({ id: sd.id, name: sd.name }))}
                defaultSedeId={filterLocation === 'all' ? null : filterLocation}
                onAddAppointment={handleAddAppointment}
                maxOverridesPerSlot={maxOverridesPerSlot}
                services={agendaServices}
                onQuickAdd={handleQuickAdd}
              />
            ) : (
              <div className="bg-white rounded-xl p-6 text-center">
                <p className="text-gray-600">Vista Agenda no disponible</p>
              </div>
            )}
          </div>
        )}

        {/* Vista Lista */}
        {viewMode === 'list' && (() => {
          const todayStr = `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1).toString().padStart(2, '0')}-${selectedDate.getDate().toString().padStart(2, '0')}`;
          const todayAppointments = displayAppointments.filter(apt => apt.appointment_date.split('T')[0] === todayStr);
          return (
          <div className="mt-4 bg-white rounded-2xl shadow-xl p-4 cal-baja">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Turnos del {selectedDate.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })} ({todayAppointments.length})</h2>

            {todayAppointments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">{filterLocation !== 'all' ? 'Sin turnos en esta sede para este día' : 'No hay turnos para este día'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todayAppointments
                  .sort((a, b) => a.appointment_time.localeCompare(b.appointment_time))
                  .map((apt, i) => (
                    <div
                      key={apt.id}
                      onClick={() => handleAppointmentClick(apt)}
                      style={{ ['--i' as string]: Math.min(i, 8) }}
                      className={`cal-turno border rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer ${
                        apt.status === 'cancelled'
                          ? 'border-gray-200 bg-gray-50 opacity-60'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h3 className={`font-bold ${apt.status === 'cancelled' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{apt.client_display_name}</h3>
                          <p className={`text-sm ${apt.status === 'cancelled' ? 'text-gray-400 line-through' : 'text-gray-600'}`}>{getServiceDisplay(apt)}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusStyle(apt.status)}`}>
                          {getStatusText(apt.status)}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                        <div>
                          <span className="font-medium">Fecha:</span> {parseLocalDate(apt.appointment_date).toLocaleDateString('es-AR')}
                        </div>
                        <div>
                          <span className="font-medium">Hora:</span> {apt.appointment_time.substring(0, 5)}
                        </div>
                        <div>
                          <span className="font-medium">Duración:</span> {apt.duration} min
                        </div>
                        <div>
                          <span className="font-medium">Precio:</span> {formatPrice(apt.price)}
                        </div>
                        {apt.location_name && (
                          <div className="col-span-2">
                            <span className="font-medium flex items-center gap-1"><MapPin className="w-4 h-4" /> Sede:</span> {apt.location_name}{apt.location_city ? ` - ${apt.location_city}` : ''}
                          </div>
                        )}
                      </div>

                      {/* Nota del turno: editable en la misma tarjeta */}
                      <AppointmentNoteEditor
                        appointmentId={apt.id}
                        value={apt.notes}
                        compact
                        onSaved={(newNotes) => {
                          setAppointments(prev => prev.map(a =>
                            a.id === apt.id ? { ...a, notes: newNotes } : a
                          ));
                        }}
                      />

                      {apt.patient_extra_data && Object.keys(apt.patient_extra_data).length > 0 && (
                        <div className="text-xs text-gray-500 bg-amber-50/50 rounded-lg p-2 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                          {Object.entries(apt.patient_extra_data).filter(([key]) => !['additional_service_ids', 'all_service_names'].includes(key)).map(([key, value]: [string, any]) => (
                            value && (
                              <span key={key}>
                                <span className="font-bold">{getExtraLabel(key)}:</span> {formatExtraValue(key, value)}
                              </span>
                            )
                          ))}
                        </div>
                      )}

                      {/* Indicadores de notificaciones */}
                      <NotificationIndicators appointment={apt} compact={true} />
                    </div>
                  ))}
              </div>
            )}
          </div>
          );
        })()}
      </div>

      {/* Mini Calendario Slide (para vista agenda) */}
      {MiniCalendarSlide && showMiniCalendar && (
        <MiniCalendarSlide
          isOpen={showMiniCalendar}
          onClose={() => setShowMiniCalendar(false)}
          selectedDate={selectedDate}
          onDateSelect={(date: Date) => {
            setSelectedDate(date);
            setShowMiniCalendar(false);
          }}
          appointmentDates={appointmentDates}
          workingDays={workingDays}
        />
      )}

      {/* Modal de detalle del día (vista mes) */}
      {viewMode === 'month' && showModal && selectedDay && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-3xl md:rounded-3xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl animate-slide-up">
            {/* Header del modal */}
            <div className={`bg-gradient-to-r ${colors.modalGradient} p-6 text-white`}>
              <h3 className="text-2xl font-bold mb-2 flex items-center gap-2">
                <CalendarDays className="w-6 h-6" /> {selectedDay} de {MONTHS[currentMonth]}
              </h3>
              <p className="text-white/90">
                {getAppointmentsForDay(selectedDay).length} {getAppointmentsForDay(selectedDay).length === 1 ? 'turno' : 'turnos'} programados
              </p>
              {isHoliday(selectedDay) && (
                <p className="text-white/90 text-sm mt-2 flex items-center gap-1">
                  <PartyPopper className="w-4 h-4" /> {getHolidayName(selectedDay)}
                </p>
              )}
            </div>

            {/* Lista de citas */}
            <div className="p-6 overflow-y-auto max-h-96 space-y-4">
              {getAppointmentsForDay(selectedDay).map((apt) => (
                <div
                  key={apt.id}
                  className={`rounded-2xl p-4 border-2 shadow-sm hover:shadow-md transition-all ${
                    apt.status === 'cancelled'
                      ? 'bg-gray-50 border-gray-200 opacity-60'
                      : 'bg-gradient-to-br from-gray-50 to-white border-gray-100'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{getStatusIcon(apt.status)}</span>
                      <span className={`text-xl font-bold ${apt.status === 'cancelled' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {apt.appointment_time.substring(0, 5)}
                      </span>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      apt.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      apt.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      apt.status === 'cancelled' ? 'bg-gray-200 text-gray-500' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {apt.status === 'confirmed' ? 'Confirmado' :
                       apt.status === 'pending' ? 'Pendiente' :
                       apt.status === 'completed' ? 'Completado' :
                       'Cancelado'}
                    </span>
                  </div>

                  <div className="space-y-1">
                    <p className={`text-lg font-semibold ${apt.status === 'cancelled' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {apt.client_display_name}
                    </p>
                    <p className={apt.status === 'cancelled' ? 'text-gray-400 line-through' : 'text-gray-600'}>
                      {getServiceDisplay(apt)}
                    </p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {apt.duration} min
                      </span>
                      <span className="text-lg font-bold text-green-600">
                        {formatPrice(apt.price)}
                      </span>
                    </div>
                    {apt.location_name && (
                      <p className="text-sm text-purple-600 mt-1 flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {apt.location_name}{apt.location_city ? ` - ${apt.location_city}` : ''}
                      </p>
                    )}
                    {/* Nota del turno: editable en la misma tarjeta */}
                    <AppointmentNoteEditor
                      appointmentId={apt.id}
                      value={apt.notes}
                      compact
                      onSaved={(newNotes) => {
                        setAppointments(prev => prev.map(a =>
                          a.id === apt.id ? { ...a, notes: newNotes } : a
                        ));
                      }}
                    />

                    {apt.patient_extra_data && Object.keys(apt.patient_extra_data).length > 0 && (
                      <div className="text-xs text-gray-500 bg-amber-50/50 rounded-lg p-2 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                        {Object.entries(apt.patient_extra_data).filter(([key]) => !['additional_service_ids', 'all_service_names'].includes(key)).map(([key, value]: [string, any]) => (
                          value && (
                            <span key={key}>
                              <span className="font-bold">{getExtraLabel(key)}:</span> {formatExtraValue(key, value)}
                            </span>
                          )
                        ))}
                      </div>
                    )}

                    {/* Indicadores de notificaciones */}
                    <NotificationIndicators appointment={apt} compact={true} />

                    {/* Botón Sobreturno */}
                    {maxOverridesPerSlot > 0 && apt.status !== 'cancelled' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowModal(false);
                          const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
                          handleAddAppointment(dateStr, apt.appointment_time.substring(0, 5));
                        }}
                        className="mt-2 w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-700 font-medium text-sm transition-colors"
                      >
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold">+ST</span>
                        Agregar sobreturno a las {apt.appointment_time.substring(0, 5)}
                      </button>
                    )}

                    {/* Botones de acción según el estado */}
                    {apt.status !== 'completed' && apt.status !== 'cancelled' && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex flex-wrap gap-2">
                          {apt.status === 'pending' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConfirmWithTransferCheck(apt);
                              }}
                              disabled={actionLoading}
                              className="flex-1 min-w-[100px] bg-green-100 text-green-800 py-2 px-3 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1"
                            >
                              <CheckCircle className="w-4 h-4" /> Confirmar
                            </button>
                          )}

                          {(apt.status === 'pending' || apt.status === 'confirmed') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAppointmentAction(apt.id, 'completed');
                              }}
                              disabled={actionLoading}
                              className="flex-1 min-w-[100px] bg-blue-100 text-blue-800 py-2 px-3 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1"
                            >
                              <CheckCircle className="w-4 h-4" /> Completar
                            </button>
                          )}

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const reason = prompt('Motivo de cancelación (opcional):');
                              if (reason !== null) { // null = usuario canceló el prompt
                                handleAppointmentAction(apt.id, 'cancelled', { cancellationReason: reason || 'Sin motivo especificado' });
                              }
                            }}
                            disabled={actionLoading}
                            className="flex-1 min-w-[100px] bg-red-100 text-red-800 py-2 px-3 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1"
                          >
                            <XCircle className="w-4 h-4" /> Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Botón crear HC (solo salud) */}
                    {professionalType === 'salud' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const params = new URLSearchParams();
                          params.set('patient', apt.client_display_name);
                          if (apt.patient_extra_data) {
                            const extra = apt.patient_extra_data;
                            if (extra.dni) params.set('dni', extra.dni);
                            if (Object.keys(extra).length > 0) {
                              sessionStorage.setItem('appointmentExtraData', JSON.stringify(extra));
                            }
                          }
                          if (apt.client_email) params.set('email', apt.client_email);
                          if (apt.client_phone) params.set('phone', apt.client_phone);
                          params.set('fromAppointment', 'true');
                          navigate(`/professional/clinical-records/new?${params.toString()}`);
                        }}
                        className="mt-2 w-full flex items-center justify-center gap-1.5 text-sm text-purple-700 font-bold bg-purple-50 hover:bg-purple-100 py-2 px-3 rounded-lg transition-colors"
                      >
                        <ClipboardList className="w-4 h-4" />
                        Crear HC con estos datos
                      </button>
                    )}

                    {/* Esta pantalla la usa también la secretaria
                        (/assistant/calendar). El botón sale solo si la cuenta
                        puede emitir: una receta la firma el médico con su
                        matrícula, no la agenda. */}
                    {mostrarBotonReceta && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const params = new URLSearchParams({ nuevaReceta: '1' });
                          params.set('nombre', apt.client_display_name || '');
                          const extra = apt.patient_extra_data || {};
                          if (extra.dni) params.set('dni', String(extra.dni));
                          if (extra.sexo) params.set('sexo', String(extra.sexo));
                          if (extra.fecha_nacimiento) params.set('nacimiento', String(extra.fecha_nacimiento));
                          // Sin matrícula no se puede emitir, pero el botón se
                      // muestra igual: escondido, el médico nunca se entera de
                      // que existe. Lo lleva a cargarla, que es el camino que
                      // queremos.
                      navigate(
                        puedeEmitirRecetas
                          ? `/professional/prescriptions?${params.toString()}`
                          : '/professional/habilitar-recetas'
                      );
                        }}
                        className="mt-2 w-full flex items-center justify-center gap-1.5 text-sm text-teal-700 font-bold bg-teal-50 hover:bg-teal-100 py-2 px-3 rounded-lg transition-colors"
                      >
                        <FileSignature className="w-4 h-4" />
                        Crear receta
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer del modal */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              {actionLoading && (
                <div className="mb-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Actualizando cita...</p>
                </div>
              )}
              
              <button
                onClick={() => setShowModal(false)}
                disabled={actionLoading}
                className="w-full bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all disabled:opacity-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detalle del turno: el mismo componente que en la computadora (DetalleTurno) */}
      {showAppointmentDetail && selectedAppointment && (
        <DetalleTurno
          turno={selectedAppointment}
          servicio={getServiceDisplay(selectedAppointment)}
          precio={formatPrice(selectedAppointment.price)}
          onCerrar={() => setShowAppointmentDetail(false)}
          onWhatsApp={() => handleWhatsAppClick(selectedAppointment)}
          notaDelTurno={
            <AppointmentNoteEditor
              appointmentId={selectedAppointment.id}
              value={selectedAppointment.notes}
              onSaved={(newNotes) => {
                setSelectedAppointment(prev => prev ? { ...prev, notes: newNotes } : prev);
                setAppointments(prev => prev.map(a =>
                  a.id === selectedAppointment.id ? { ...a, notes: newNotes } : a
                ));
              }}
            />
          }
          notificaciones={<NotificationIndicators appointment={selectedAppointment} compact={false} />}
          tipo={professionalType === 'belleza' ? 'belleza' : 'salud'}
          clinica={{ historia: professionalType === 'salud', receta: mostrarBotonReceta, puedeEmitir: puedeEmitirRecetas }}
          acciones={{
            cargando: actionLoading,
            onConfirmar: () => handleConfirmWithTransferCheck(selectedAppointment),
            onCompletar: () => handleAppointmentAction(selectedAppointment.id, 'completed'),
            onReagendar: () => { setShowAppointmentDetail(false); setShowRescheduleModal(true); },
            onCancelar: () => {
              const reason = prompt('Motivo de cancelación (opcional):');
              if (reason !== null) {
                handleAppointmentAction(selectedAppointment.id, 'cancelled', { cancellationReason: reason || 'Sin motivo especificado' });
              }
            },
            onBorrar: () => setConfirmHideOpen(true),
            onSobreturno: maxOverridesPerSlot > 0 ? () => {
              const date = selectedAppointment.appointment_date.split('T')[0];
              const time = selectedAppointment.appointment_time.substring(0, 5);
              setShowAppointmentDetail(false);
              setSelectedAppointment(null);
              handleAddAppointment(date, time);
            } : undefined,
          }}
        />
      )}

      {/* Confirmación de borrado (soft-hide reversible) */}
      <ConfirmModal
        open={confirmHideOpen}
        title="Borrar turno cancelado"
        message="Se quitará de la agenda este turno cancelado. Queda guardado por si necesitás recuperarlo."
        confirmLabel="Borrar"
        onConfirm={handleHideAppointment}
        onClose={() => setConfirmHideOpen(false)}
      />

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>

      {/* Modal de reagendamiento */}
      <RescheduleModal
        isOpen={showRescheduleModal}
        onClose={() => { setShowRescheduleModal(false); setSelectedAppointment(null); }}
        appointment={selectedAppointment ? {
          id: selectedAppointment.id,
          professional_id: selectedAppointment.professional_id,
          service_name: selectedAppointment.service_name,
          appointment_date: selectedAppointment.appointment_date?.split?.('T')?.[0] || selectedAppointment.appointment_date,
          appointment_time: selectedAppointment.appointment_time,
          duration: selectedAppointment.duration || 30,
          price: selectedAppointment.price || 0,
          client_name: selectedAppointment.client_display_name,
          status: selectedAppointment.status,
          location_id: selectedAppointment.location_id,
        } : null}
        onSuccess={() => { setShowRescheduleModal(false); setSelectedAppointment(null); window.location.reload(); }}
        skipValidation={true}
      />

      {/* Modal de verificación de transferencia */}
      {showTransferModal && pendingTransferAppointment && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-4">
              <h3 className="text-lg font-bold text-amber-900 flex items-center gap-2">
                <Building2 className="w-5 h-5" /> Turno con Transferencia
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Este turno de <span className="font-semibold">{pendingTransferAppointment.client_display_name}</span> fue agendado con pago por <span className="font-semibold text-amber-700">transferencia bancaria</span>.
              </p>
              <p className="text-gray-600 text-sm mb-6">
                ¿Deseas ir al verificador de transferencias para confirmar el pago antes de confirmar el turno?
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setPendingTransferAppointment(null);
                    setShowAppointmentDetail(false);
                    setShowModal(false);
                    navigate('/professional/payment-verification');
                  }}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white py-3 px-4 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" /> Sí, verificar transferencia
                </button>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    const apt = pendingTransferAppointment;
                    setPendingTransferAppointment(null);
                    handleAppointmentAction(apt.id, 'confirmed');
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors"
                >
                  Confirmar sin verificar pago
                </button>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setPendingTransferAppointment(null);
                  }}
                  className="w-full text-gray-500 hover:text-gray-700 py-2 text-sm transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MobileMonthCalendar;